import React from 'react';
import PropTypes from 'prop-types';
import { Input, Form, message } from 'antd';
import { useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';

import Modal from 'common/components/modal';

import {
  MUTATION_CREATE_LANDLORD_SUBSCRIPTION,
  MUTATION_UPDATE_LANDLORD_SUBSCRIPTION,
} from '../query';

const URL_REGEXP =
  /(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/[a-zA-Z0-9]{2,}|((https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?)|(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})?/;

const useStyles = makeStyles(theme => ({
  modalWrapper: () => ({
    '& .ant-modal-body': {
      padding: '0 32px 8px 32px',
    },

    '& .ant-form-item-label': {
      lineHeight: '22px !important',
      color: theme.colors.titleColor,
      marginBottom: 8,
    },

    '& .ant-modal-footer': {
      '& .ant-btn-background-ghost': {
        borderColor: theme.colors.gray5,
        color: theme.colors.bodyColor,
        fontFamily: "'Avenir Next W04', 'Avenir Next', helvetica, arial, sans-serif",
        boxShadow: 'none',

        '& span': {
          fontWeight: 400,
        },
      },
    },
  }),
}));

const CreateWebHookModal = ({
  t,
  visible,
  form,
  landlord,
  handleOpenOrCloseModal,
  handleSuccessCallback,
}) => {
  const { getFieldDecorator, validateFields } = form;

  const isEdit = landlord?.webhook !== null;

  const classes = useStyles();

  const [createWebhookUrl, { loading: createLoading }] = useMutation(
    MUTATION_CREATE_LANDLORD_SUBSCRIPTION,
    {
      onCompleted: result => {
        if (result?.createLandlordSubscription?.callbackUrl) {
          handleOpenOrCloseModal();
          handleSuccessCallback(result?.createLandlordSubscription?.callbackUrl);
        }
      },
      onError: e => {
        if (e?.message === 'NOT_EXIST_API_TOKEN') {
          message.error(t('cms.dashboard.landlord.details.token.webhook.error_token_message'));
        } else if (e) {
          message.error(e.toString());
        } else {
          message.error(t('Something wrong, please try again.'));
        }
      },
    },
  );

  const [updateWebhookUrl, { loading: updateLoading }] = useMutation(
    MUTATION_UPDATE_LANDLORD_SUBSCRIPTION,
    {
      onCompleted: result => {
        if (result?.updateLandlordSubscription?.callbackUrl) {
          handleOpenOrCloseModal();
          handleSuccessCallback(result?.updateLandlordSubscription?.callbackUrl);
        }
      },
      onError: e => {
        if (e?.message === 'NOT_EXIST_API_TOKEN') {
          message.error(t('cms.dashboard.landlord.details.token.webhook.error_token_message'));
        } else if (e) {
          message.error(e.toString());
        } else {
          message.error(t('Something wrong, please try again.'));
        }
      },
    },
  );

  const handleConfirmClick = () => {
    validateFields((err, values) => {
      if (!err) {
        const { url } = values;

        const params = {
          variables: {
            input: {
              callbackUrl: url,
              name: landlord.name,
              landlordId: landlord.id,
            },
          },
        };

        if (isEdit) {
          updateWebhookUrl(params);
        } else {
          createWebhookUrl(params);
        }
      }
    });
  };

  return (
    <Modal
      visible={visible}
      okText={t('cms.properties.edit.leave_alert.title')}
      width={454}
      title={t(
        `cms.dashboard.landlord.details.token.webhook.${isEdit ? 'edit' : 'create'}_modal.title`,
      )}
      wrapClassName={classes.modalWrapper}
      onCancel={handleOpenOrCloseModal}
      onOk={handleConfirmClick}
      okButtonProps={{
        loading: createLoading || updateLoading,
      }}
    >
      <Form.Item label={'URL'}>
        {getFieldDecorator('url', {
          initialValue: landlord?.webhook?.callbackUrl,
          rules: [
            {
              required: true,
              message: t('cms.dashboard.landlord.details.token.webhook.error_message'),
            },
            {
              validator: (_, value, callback) => {
                const valid = URL_REGEXP.test(value);

                if (value && !valid) {
                  return callback(t('cms.dashboard.landlord.details.token.webhook.error_message'));
                }

                return callback();
              },
            },
          ],
        })(<Input />)}
      </Form.Item>
    </Modal>
  );
};

CreateWebHookModal.propTypes = {
  t: PropTypes.func.isRequired,
  handleCreateTokenApi: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  handleOpenOrCloseModal: PropTypes.func.isRequired,
};

export default Form.create({
  name: 'CreateWebHookModal',
})(CreateWebHookModal);
